import { StIdentityUser } from '../../../services/socialTradingService';

export function mergeIdentityUsers(...lists: StIdentityUser[][]): StIdentityUser[] {
  const seen = new Set<string>();
  return lists
    .reduce<StIdentityUser[]>((all, list) => all.concat(list), [])
    .filter((item) => {
      if (!item?.userId || seen.has(item.userId)) return false;
      seen.add(item.userId);
      return true;
    });
}

/** KOL_ROOM owners may be KOL or OFFICIAL; official rooms stay OFFICIAL-only. */
export function ownerOptionsForRoomType(
  type: string,
  officialOwners: StIdentityUser[],
  kolOwners: StIdentityUser[],
): StIdentityUser[] {
  if (type === 'KOL_ROOM') {
    return mergeIdentityUsers(kolOwners, officialOwners);
  }
  return officialOwners;
}

/** Official rooms (not KOL_ROOM) cannot be owned by a current KOL. */
export function kolCannotOwnOfficialRoom(
  type: string,
  ownerUserId: string,
  kolOwners: StIdentityUser[],
): boolean {
  if (type === 'KOL_ROOM') return false;
  const id = ownerUserId.trim().toLowerCase();
  if (!id) return false;
  return kolOwners.some((u) => u.userId.toLowerCase() === id && u.role === 'KOL');
}

export const OWNER_KOL_ROLE_CONFLICT_MESSAGE = 'cannot assign KOL user as official room owner';

export function isOwnerKolRoleConflictMessage(message: string): boolean {
  return message.includes(OWNER_KOL_ROLE_CONFLICT_MESSAGE);
}

export const SMART_MONEY_REQUIRES_PUBLIC_MESSAGE = 'smart money room must be public';

export function isSmartMoneyRequiresPublicMessage(message: string): boolean {
  return message.includes(SMART_MONEY_REQUIRES_PUBLIC_MESSAGE);
}

/** Room-list filter value → API param. Both true and false filter; anything else means "all". */
export function smartMoneyFilterParam(value: unknown): boolean | undefined {
  if (value === true || value === 'true') return true;
  if (value === false || value === 'false') return false;
  return undefined;
}

export function identityOwnerLabel(option: StIdentityUser | string): string {
  if (typeof option === 'string') return option;
  const name = option.nickname || option.userId;
  return option.role ? `${name} [${option.role}] (${option.userId})` : `${name} (${option.userId})`;
}

/**
 * Announcements are capped at 400 **bytes** of UTF-8, not 200 characters: the
 * value is synced into Tencent IM's `Notification` field, which counts bytes.
 * Whitespace counts too, and is no longer stripped before the check.
 */
export const ANNOUNCEMENT_MAX_BYTES = 400;

/**
 * UTF-8 byte length — the same unit the server rejects on.
 *
 * `text.length` is UTF-16 and undercounts CJK threefold, which is how a "200
 * character" announcement used to fail three services away.
 */
export function announcementByteLength(text: string): number {
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(text).length;
  // Only reached in environments without TextEncoder; unescape/encodeURIComponent
  // counts the same bytes.
  return encodeURIComponent(text).replace(/%[0-9A-F]{2}/gi, 'x').length;
}

export const announcementOverBudget = (text: string): boolean =>
  announcementByteLength(text) > ANNOUNCEMENT_MAX_BYTES;

/**
 * True while a translation of the announcement is still running.
 *
 * The backend still accepts a whitespace-only reformat during the lock, so this
 * may only inform — never disable saving on it.
 */
export function announcementEditLocked(lockedUntil?: string | null, now = Date.now()): boolean {
  if (!lockedUntil) return false;
  const until = new Date(lockedUntil).getTime();
  return Number.isFinite(until) && until > now;
}
